/* @flow */

import { HeaderName } from '../../libs/netgemLibrary/v8/constants/Headers';
import { HttpStatus } from '../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { HttpMethod } from '../../libs/netgemLibrary/v8/types/HttpMethod';
import { logError } from '../debug/debug';
import { Messenger, MessengerEvents } from '../messenger';
import { OtpResponseKind, PIN_SUCCESS_STATUS } from './constsAndTypes';
import { fetchHttpStatus, fetchJson } from './helpers';
import ParentalControlManager from './parentalControlManager';

type PinCodeStatus = {
  contact: boolean,
  hasPinCode: boolean,
};

class ParentalControlApi {
  #fetchOptions: any;
  #otpUrl: string; // POST
  #checkUrl: string; // GET
  #existsUrl: string; // GET
  #adminUrl: string; // PUT | DELETE

  constructor(baseUrl: string) {
    this.#otpUrl = new URL('otp', baseUrl).toString();
    this.#checkUrl = new URL('admin/check', baseUrl).toString();
    this.#existsUrl = new URL('admin/exists', baseUrl).toString();
    this.#adminUrl = new URL('admin', baseUrl).toString();

    Messenger.on(MessengerEvents.AUTHENTICATION_TOKEN_CHANGED, this.updateAuthenticationToken);
  }

  updateAuthenticationToken = async (authenticationToken: string | null): Promise<void> => {
    if (authenticationToken === null) {
      this.#fetchOptions = null;
      return;
    }

    this.#fetchOptions = {
      headers: new Headers({ [HeaderName.Authorization]: `Bearer ${authenticationToken ?? ''}` }),
    };

    await ParentalControlManager.onNewAuthToken();
  };

  getPinCodeStatus = async (): Promise<PinCodeStatus> => {
    if (this.#fetchOptions === null) {
      // Authentication token is null
      return { contact: false, hasPinCode: false };
    }

    try {
      const { contact, status } = await fetchJson(this.#existsUrl, this.#fetchOptions);

      return {
        contact: Boolean(contact),
        hasPinCode: OtpResponseKind.cast(status) === OtpResponseKind.Success,
      };
    } catch (error) {
      logError(`Error in hasPinCode(): ${error.message}`);
      return { contact: false, hasPinCode: false };
    }
  };

  askOtp = async (): Promise<boolean> => {
    try {
      const status = await fetchHttpStatus(this.#otpUrl, { ...this.#fetchOptions, method: HttpMethod.POST });
      return status === HttpStatus.Created || status === HttpStatus.NoContent;
    } catch (error) {
      logError(`Error in askOtp(): ${error.message}`);
      return false;
    }
  };

  checkPinCode = async (code: string): Promise<{ isCorrect: boolean, retryAfter: number, tooManyRequests?: boolean }> => {
    try {
      const url = new URL(this.#checkUrl);
      url.searchParams.set('code', code);

      const { retryAfter, status, tooManyRequests } = await fetchJson(url.toString(), this.#fetchOptions);
      return { isCorrect: status === PIN_SUCCESS_STATUS, retryAfter: retryAfter ? Number(retryAfter) : 0, tooManyRequests };
    } catch (error) {
      logError(`Error in checkPinCode(): ${error.message}`);
      return { isCorrect: false, retryAfter: 0 };
    }
  };

  setPinCode = async (otp: string, code: string): Promise<OtpResponseKind> => {
    try {
      const url = new URL(this.#adminUrl);
      url.searchParams.set('otp', otp);
      url.searchParams.set('code', code);

      const { status } = await fetchJson(url.toString(), { ...this.#fetchOptions, method: HttpMethod.PUT });
      const response = OtpResponseKind.cast(status);

      if (response === undefined) {
        throw new Error(`Response status not expected: ${status ?? 'undefined'}`);
      }

      return response;
    } catch (error) {
      logError(`Error in setPinCode(): ${error.message}`);
      return OtpResponseKind.Error;
    }
  };

  deletePinCode = async (otp: string): Promise<OtpResponseKind> => {
    try {
      const url = new URL(this.#adminUrl);
      url.searchParams.set('otp', otp);

      const { status } = await fetchJson(url.toString(), { ...this.#fetchOptions, method: HttpMethod.DELETE });
      const response = OtpResponseKind.cast(status);

      if (response === undefined) {
        throw new Error(`Response status not expected: ${status ?? 'undefined'}`);
      }

      return response;
    } catch (error) {
      logError(`Error in deletePinCode(): ${error.message}`);
      return OtpResponseKind.Error;
    }
  };
}

export default ParentalControlApi;
